import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import {
    addCategorizationToVegobjekttype,
    removeVOTCategory,
    setVOTCategory,
    isCategorizing,
} from '@/actions/vegobjekterActions';
import { Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { RootState, UserState, VegobjekttypeState } from '@/state';
import { hasAccess } from '@/utils/Utils';
import * as selectors from '../../../../selectors/selectors';
import CategoryInterval from './CategoryInterval';

interface TagProps {
    vegobjekttype: Vegobjekttype;
    vegobjekttypeData: VegobjekttypeState;
}

interface DispatchProps {
    doRemoveCategorization: (votState: VegobjekttypeState) => void;
    doAddCategorization: (votState: VegobjekttypeState) => void;
    doSetCategorizationOnProperty: (
        votState: VegobjekttypeState,
        egenskapstype: Egenskapstype,
        interval?: number[] | null,
        absoluteIntervals?: boolean
    ) => void;
    doSetIsCategorizing: (categorizing: boolean) => void;
}

interface StateProps {
    isCategorizing: boolean;
    user: UserState;
}

type Props = TagProps & StateProps & DispatchProps;

const mapDispatchToProps = {
    doAddCategorization: addCategorizationToVegobjekttype,
    doRemoveCategorization: removeVOTCategory,
    doSetCategorizationOnProperty: setVOTCategory,
    doSetIsCategorizing: isCategorizing,
};

const mapStateToProps = (state: RootState): StateProps => ({
    isCategorizing: selectors.stateIsCategorizing(state),
    user: selectors.stateUser(state),
});

const CategorizeComponent: React.FC<Props> = ({
    vegobjekttype,
    vegobjekttypeData,
    isCategorizing,
    user,
    doRemoveCategorization,
    doSetCategorizationOnProperty,
    doSetIsCategorizing,
}) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                wrapperRef.current &&
                !wrapperRef.current.contains(event.target as Node)
            ) {
                setOpen(false);
                if (isCategorizing) {
                    doSetIsCategorizing(false);
                }
            }
        };

        document.addEventListener('click', handleClickOutside, { capture: true });
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [isCategorizing, doSetIsCategorizing]);

    const chooseAttributeType = (value: number) => {
        const egenskapstype =
            vegobjekttype.egenskapstyper.find(e => e.id === value) || null;
        if (
            egenskapstype === null ||
            !egenskapstype.erNummerisk() ||
            egenskapstype.erTallEnum()
        ) {
            setOpen(false);
        }
        if (egenskapstype === null) {
            doRemoveCategorization(vegobjekttypeData);
        } else {
            doSetCategorizationOnProperty(vegobjekttypeData, egenskapstype);
        }
        doSetIsCategorizing(true);
    };

    const setInterval = (
        egenskapstype: Egenskapstype,
        interval: number[],
        absoluteIntervals: boolean
    ) => {
        setOpen(false);
        doSetCategorizationOnProperty(
            vegobjekttypeData,
            egenskapstype,
            interval,
            absoluteIntervals
        );
    };

    const isNotAvailable = (sensitivity: number, id: number) => {
        return (
            !hasAccess(sensitivity, user.roles) ||
            [521, 524, 525, 526, 562, 641, 644, 645, 793].includes(id)
        );
    };

    const category = vegobjekttypeData.category;
    const selectedEgenskap = category ? category : null;
    const buttonText = category && category.egenskapstype ? category.navn : 'Kategoriser';

    return (
        <div className={'c-categorize-container'} ref={wrapperRef}>
            <button
                className={'c-hva-info__button'}
                value="categorize"
                id="categorize_button"
                onClick={() => {
                    setOpen(!open);
                    doSetIsCategorizing(!open);
                }}
                aria-label={
                    category?.egenskapstype
                        ? `Kategorisert etter ${buttonText}`
                        : buttonText
                }
                aria-description={'Trykk for å endre kategorisering'}
                aria-expanded={open}
            >
                <i className="o-icon-category c-categorize__icon" />
                <span className="c-advanced-filter__toggle-label">{buttonText}</span>
            </button>
            {open && (
                <div className="c-category-selector">
                    <label className="c-category-selector__label">
                        Kategoriser etter egenskapstype
                    </label>
                    {selectedEgenskap &&
                        selectedEgenskap.erNummerisk() &&
                        !selectedEgenskap.erEnum() && (
                            <CategoryInterval
                                interval={vegobjekttypeData.intervals}
                                egenskapstype={selectedEgenskap}
                                absoluteIntervals={vegobjekttypeData.absoluteIntervals}
                                setInterval={(i, abs) =>
                                    setInterval(selectedEgenskap, i, abs)
                                }
                            />
                        )}
                    <ul aria-label="Kategoriserbare egenskaper">
                        <li key={'null'}>
                            <button
                                className="c-category-selector__button"
                                title="Ingen kategorisering"
                                value=" "
                                onClick={event => {
                                    chooseAttributeType(
                                        parseInt(event.currentTarget.value, 10)
                                    );
                                }}
                            >
                                Ingen kategorisering
                            </button>
                        </li>
                        {vegobjekttype.egenskapstyper
                            .filter(e => e.erEnum() || e.erNummerisk())
                            .sort(Egenskapstype.comparator)
                            .map(e => (
                                <li key={e.id}>
                                    <button
                                        disabled={isNotAvailable(e.sensitivitet, e.id)}
                                        onClick={event =>
                                            chooseAttributeType(
                                                parseInt(event.currentTarget.value, 10)
                                            )
                                        }
                                        className={`c-category-selector__button ${
                                            isNotAvailable(e.sensitivitet, e.id)
                                                ? ' disabled'
                                                : ''
                                        }`}
                                        value={e.id}
                                        id={'category_selector_button_' + e.id}
                                    >
                                        {`${e.navn}${
                                            isNotAvailable(e.sensitivitet, e.id)
                                                ? ' (Ikke tilgjengelig)'
                                                : ''
                                        }`}
                                    </button>
                                </li>
                            ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export const Categorize = connect(
    mapStateToProps,
    mapDispatchToProps
)(CategorizeComponent);
